import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Camera, X, Upload, Loader2, Sparkles, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Upload a single photo to /api/files/upload and emit the relative URL.
 * `value` is the URL (e.g. "/api/files/<id>"). `onChange` receives the new URL or "".
 * Renders a thumbnail with delete on top. Clicking the thumbnail opens a lightbox.
 *
 * When `aiContext` is provided, shows a "Validar com IA" button that asks the
 * server to analyze the photo and renders the verdict inline.
 *
 * FASE 20:
 * - `allowGallery=false` (default) forces the mobile camera (capture attr).
 * - `referencePhotoUrl` is sent to the AI so it compares against the standard.
 * - `showAiValidate=false` hides the AI button (e.g. reference photo uploads).
 */
export default function PhotoUpload({
  value,
  onChange,
  disabled,
  testId,
  aiContext,
  onVerdict,
  initialVerdict,
  allowGallery = false,
  referencePhotoUrl = "",
  showAiValidate = true,
}) {
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [verdict, setVerdict] = useState(initialVerdict || null);
  const [validating, setValidating] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  // Sync external verdict updates (e.g. read-only load)
  useEffect(() => {
    if (initialVerdict) setVerdict(initialVerdict);
  }, [initialVerdict]);

  // Fetch thumbnail securely (needs Authorization header — can't use <img src> directly)
  useEffect(() => {
    let revoked = null;
    if (value) {
      (async () => {
        try {
          const resp = await api.get(value.replace(/^\/api/, ""), { responseType: "blob" });
          const url = URL.createObjectURL(resp.data);
          revoked = url;
          setBlobUrl(url);
        } catch (err) {
          console.error("photo preview failed", err);
        }
      })();
    } else {
      setBlobUrl(null);
      setVerdict(null);
      if (onVerdict) onVerdict(null);
    }
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [value]);

  const handlePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Imagem maior que 5MB");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("purpose", "evidence");
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      setVerdict(null);
      toast.success("Foto enviada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no upload");
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleRemove = () => {
    onChange("");
    setVerdict(null);
  };

  const validateWithAI = async () => {
    if (!value) return;
    setValidating(true);
    setVerdict(null);
    try {
      const { data } = await api.post("/ai/validate-image", {
        photo_url: value,
        question_context: aiContext || "",
        reference_photo_url: referencePhotoUrl || "",
      });
      setVerdict(data);
      if (onVerdict) onVerdict(data);
      if (data.compliant) {
        toast.success("IA: conforme");
      } else {
        toast.error("IA detectou não conformidade");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha na IA");
    } finally {
      setValidating(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      {value && blobUrl ? (
        <div className="relative inline-block">
          <img
            src={blobUrl}
            alt="evidência"
            className="h-32 w-auto rounded-md border border-stone-200 object-cover cursor-zoom-in"
            onClick={() => setLightbox(true)}
            data-testid={testId ? `${testId}-thumb` : undefined}
          />
          <Dialog open={lightbox} onOpenChange={setLightbox}>
            <DialogContent className="max-w-4xl p-2 bg-stone-950 border-stone-800" data-testid={testId ? `${testId}-lightbox` : "photo-lightbox"}>
              <DialogTitle className="sr-only">Visualização da foto</DialogTitle>
              <img src={blobUrl} alt="evidência ampliada" className="w-full h-auto max-h-[80vh] object-contain rounded" />
            </DialogContent>
          </Dialog>
          {!disabled && (
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-rose-600 text-white shadow flex items-center justify-center hover:bg-rose-700"
              data-testid={testId ? `${testId}-remove` : undefined}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      ) : value && !blobUrl ? (
        <div className="text-xs text-stone-500 font-body inline-flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Carregando preview...
        </div>
      ) : null}

      {!disabled && (
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            {...(!allowGallery ? { capture: "environment" } : {})}
            onChange={handlePick}
            className="hidden"
            data-testid={testId ? `${testId}-input` : undefined}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={loading}
            className="bg-white"
            data-testid={testId ? `${testId}-button` : undefined}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : value ? (
              <Upload className="h-4 w-4 mr-2" />
            ) : (
              <Camera className="h-4 w-4 mr-2" />
            )}
            {value
              ? allowGallery ? "Trocar foto" : "Tirar outra foto"
              : allowGallery ? "Enviar foto" : "Tirar foto"}
          </Button>
          {value && showAiValidate && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={validateWithAI}
              disabled={validating}
              className="bg-white border-emerald-200 text-emerald-800 hover:bg-emerald-50"
              data-testid={testId ? `${testId}-validate` : undefined}
            >
              {validating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              {validating ? "Analisando…" : "Validar com IA"}
            </Button>
          )}
        </div>
      )}

      {/* Verdict card */}
      {verdict && (
        <div
          className={`mt-2 rounded-md border p-3 text-xs font-body max-w-md ${
            verdict.compliant
              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : "border-rose-200 bg-rose-50 text-rose-900"
          }`}
          data-testid={testId ? `${testId}-verdict` : undefined}
        >
          <div className="flex items-center gap-2 mb-1">
            {verdict.compliant ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            <span className="font-display font-bold">
              {verdict.compliant ? "Conforme" : "Não conforme"}
            </span>
            <Badge variant="outline" className="ml-1 text-[10px]">
              Confiança {Math.round((verdict.confidence || 0) * 100)}%
            </Badge>
          </div>
          {verdict.summary && <div className="mb-1.5">{verdict.summary}</div>}
          {Array.isArray(verdict.findings) && verdict.findings.length > 0 && (
            <ul className="list-disc list-inside space-y-0.5 mb-1">
              {verdict.findings.slice(0, 4).map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          )}
          {Array.isArray(verdict.recommendations) &&
            verdict.recommendations.length > 0 && (
              <div className="mt-1.5 text-[11px] italic opacity-90">
                ▸ {verdict.recommendations[0]}
              </div>
            )}
        </div>
      )}
    </div>
  );
}
